import { createContext, useState, useContext, useEffect } from 'react';
import { auth, db } from '../firebase'; 
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const UserContext = createContext({
  user: undefined,
  loading: true,
  unverifiedUser: null,
  login: async () => {},
  logout: async () => {},
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          // Check if email is verified
          if (!authUser.emailVerified) {
            // Store unverified user info but don't authenticate them
            setUnverifiedUser({
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName
            });
            setUser(null);
            navigate('/login'); // Redirect to login page
            setLoading(false);
            return;
          }
          
          // Reset unverified user state if user is now verified
          setUnverifiedUser(null);
          
          const basicUser = { uid: authUser.uid, email: authUser.email, displayName: authUser.displayName };

          // Ensure subscription exists on backend
          try {
            const token = await authUser.getIdToken();
            const resp = await fetch(
              process.env.REACT_APP_ENSURE_SUBSCRIPTION_API || 'https://asia-south1-xskills-e47b7.cloudfunctions.net/ensure_subscription',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({})
              }
            );
            // optional: log but don't block login on failure
            if (!resp.ok) {
              console.warn('ensure_subscription failed', await resp.text());
            }
          } catch (e) {
            console.warn('ensure_subscription call error', e);
          }

          try {
            const userProfileRef = doc(db, 'users', authUser.uid, 'UserProfile', authUser.uid);
            const userProfileDoc = await getDoc(userProfileRef);
            if (userProfileDoc.exists()) {
              const userProfile = userProfileDoc.data();
              setUser({ ...basicUser, ...userProfile, isOnboardingDone: true });
            } else {
              setUser(basicUser);
            }
          } catch (error) {
            setUser(basicUser);
          }
        } else {
          setUser(null);
          // Don't clear unverifiedUser here to preserve the state for the login page
        }
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate, user?.email]);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUnverifiedUser(null);
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  return (
    <UserContext.Provider value={{ user, loading, unverifiedUser, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useAuth = () => useContext(UserContext);